/**
 * JD1 · pf-mega.js v11
 * Live storefront hard fix for Boutique mega rows.
 * V10: Pins the mega panel below the full header so it never overlaps the logo/nav rows.
 * V11: Forces an opaque, isolated panel surface so PDP/collection content never bleeds through the mega background.
 *
 * V9 changes:
 * - Keeps the original Header block in the hidden staging area and CLONES into menu panels.
 *   This avoids losing the source when Horizon hydrates/re-renders the header.
 * - Adds a synthetic desktop mega panel for top-level menu items with no native sublinks.
 * - Adds live-safe CSS for synthetic panels and hover/focus visibility.
 * - More tolerant target matching: title, handleized title, href handle, id, and one unique fuzzy match.
 */

const JD1_PF_MEGA_STYLE_ID = 'jd1-pf-mega-live-style-v11';

const handleize = (str) =>
  (str || '')
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’"]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function ensureMegaStyle() {
  if (document.getElementById(JD1_PF_MEGA_STYLE_ID)) return;

  ['jd1-pf-mega-live-style-v8', 'jd1-pf-mega-live-style-v9', 'jd1-pf-mega-live-style-v10'].forEach((id) => {
    document.getElementById(id)?.remove();
  });

  const style = document.createElement('style');
  style.id = JD1_PF_MEGA_STYLE_ID;
  style.textContent = `
    .header-menu .pf-mega-zone {
      grid-column: 1 / -1;
      width: 100%;
      min-width: 0;
    }

    .header-menu .mega-menu__grid.pf-mega-grid--replaced {
      --menu-columns-desktop: 1;
      --menu-columns-tablet: 1;
      display: grid !important;
      grid-template-columns: 1fr !important;
    }

    .header-menu .mega-menu__grid.pf-mega-grid--replaced > :not(.pf-mega-zone) {
      display: none !important;
    }

    /* JD1 V10 · panel below the real header, not below the tiny menu item.
       Horizon places submenus relative to the menu item; for this card row that
       made the panel overlap the logo/header row. We pin the active mega panel
       to the viewport and compute --pf-mega-top from the header bottom in JS. */
    .header-menu .menu-list__list-item.pf-mega-ready > .menu-list__submenu.pf-mega-panel {
      --pf-mega-panel-bg: #ffffff;
      --pf-mega-panel-text: rgb(var(--color-foreground, 18 18 18));
      position: fixed !important;
      inset-block-start: var(--pf-mega-top, var(--header-height, 120px)) !important;
      inset-inline-start: 0 !important;
      inset-inline-end: auto !important;
      width: 100vw !important;
      max-width: 100vw !important;
      margin: 0 !important;
      transform: none !important;
      clip-path: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
      isolation: isolate !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      background: var(--pf-mega-panel-bg, #ffffff) !important;
      color: var(--pf-mega-panel-text, rgb(var(--color-foreground, 18 18 18))) !important;
      border-block-start: 1px solid color-mix(in srgb, var(--pf-mega-panel-text, rgb(var(--color-foreground, 18 18 18))) 10%, transparent);
      border-block-end: 1px solid color-mix(in srgb, var(--pf-mega-panel-text, rgb(var(--color-foreground, 18 18 18))) 8%, transparent);
      box-shadow: 0 22px 64px rgb(0 0 0 / 0.10);
      z-index: calc(var(--layer-header-menu, 30) + 2);
    }

    /* JD1 V11 · solid surface guard.
       Product pages can have media/content directly under a transparent header.
       This surface guarantees the card row always sits on the same opaque canvas. */
    .header-menu .menu-list__list-item.pf-mega-ready > .menu-list__submenu.pf-mega-panel::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      display: block;
      background: var(--pf-mega-panel-bg, #ffffff) !important;
      opacity: 1 !important;
      pointer-events: none;
    }

    .header-menu .menu-list__list-item.pf-mega-ready:hover > .menu-list__submenu.pf-mega-panel,
    .header-menu .menu-list__list-item.pf-mega-ready:focus-within > .menu-list__submenu.pf-mega-panel,
    .header-menu .menu-list__list-item.pf-mega-open > .menu-list__submenu.pf-mega-panel {
      --submenu-content-opacity: 1;
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      clip-path: none !important;
    }

    .header-menu .menu-list__list-item.pf-mega-ready:hover > .menu-list__submenu.pf-mega-panel .menu-list__submenu-inner,
    .header-menu .menu-list__list-item.pf-mega-ready:focus-within > .menu-list__submenu.pf-mega-panel .menu-list__submenu-inner,
    .header-menu .menu-list__list-item.pf-mega-open > .menu-list__submenu.pf-mega-panel .menu-list__submenu-inner {
      opacity: 1 !important;
      transform: none !important;
    }

    .header-menu .menu-list__submenu.pf-mega-panel .menu-list__submenu-inner {
      position: relative;
      z-index: 1;
      transform: none !important;
      opacity: var(--submenu-content-opacity, 0);
      display: block;
      width: min(100%, var(--page-width, 1450px));
      max-height: calc(100vh - var(--pf-mega-top, var(--header-height, 120px)) - 24px);
      margin-inline: auto;
      overflow-y: auto;
      overflow-x: hidden;
      background: transparent !important;
      box-sizing: border-box;
    }

    .header-menu .menu-list__submenu.pf-mega-panel .mega-menu,
    .header-menu .menu-list__submenu.pf-mega-panel .mega-menu__grid {
      width: 100%;
      max-width: none !important;
      margin-inline: 0 !important;
      background: transparent !important;
    }

    .header-menu .menu-list__submenu.pf-mega-panel .pf-mega-zone,
    .header-menu .menu-list__submenu.pf-mega-panel .pf-mega-menu {
      background: transparent !important;
    }

    .header-menu .menu-list__submenu.pf-mega-panel .mega-menu.section,
    .header-menu .menu-list__submenu.pf-mega-panel .section--full-width-margin {
      margin-inline: 0 !important;
    }

    @media screen and (min-width: 750px) {
      .header-menu .menu-list__submenu.pf-mega-panel .menu-list__submenu-inner {
        padding-block: 24px 30px;
        padding-inline: var(--section-padding-inline, 40px);
      }
    }

    @media screen and (max-width: 749px) {
      .header-menu .menu-list__list-item.pf-mega-ready > .menu-list__submenu.pf-mega-panel {
        position: static !important;
        width: 100% !important;
        max-width: 100% !important;
        box-shadow: none;
      }
    }
  `;

  document.head.appendChild(style);
}

function directMenuLink(li) {
  return li.querySelector(':scope > .menu-list__link, :scope > a.menu-list__link, :scope > button.menu-list__link, :scope > [ref="menuitem"]');
}

function directMenuTitle(li) {
  const link = directMenuLink(li);
  if (!link) return '';
  const title = link.querySelector('.menu-list__link-title');
  return (title || link).textContent || '';
}

function hrefHandle(link) {
  const href = link?.getAttribute?.('href') || '';
  if (!href || href === '#') return '';

  try {
    const url = new URL(href, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    return handleize(parts[parts.length - 1] || '');
  } catch (_error) {
    const parts = href.split('?')[0].split('#')[0].split('/').filter(Boolean);
    return handleize(parts[parts.length - 1] || '');
  }
}

function updateMegaPanelPosition() {
  const header = document.querySelector('#header-component, header-component, .header');
  if (!header) return;

  const rect = header.getBoundingClientRect();
  const top = Math.max(0, Math.round(rect.bottom));
  document.documentElement.style.setProperty('--pf-mega-top', `${top}px`);
}

function topLevelDesktopItems(menu) {
  if (!menu) return [];

  const overflow = menu.querySelector('overflow-list');
  if (overflow) {
    return [...overflow.children].filter((el) =>
      el.matches?.('.menu-list__list-item:not([slot="overflow"]):not([aria-hidden="true"])')
    );
  }

  const list = menu.querySelector('.menu-list__list');
  if (list) {
    return [...list.children].filter((el) =>
      el.matches?.('.menu-list__list-item:not([slot="overflow"]):not([aria-hidden="true"])')
    );
  }

  return [...menu.querySelectorAll('.menu-list__list-item:not([slot="overflow"]):not([aria-hidden="true"])')];
}

function buildDesktopEntries() {
  const menu = document.querySelector('.header-menu');
  const items = topLevelDesktopItems(menu);

  return items
    .map((li) => {
      const link = directMenuLink(li);
      const label = directMenuTitle(li).trim();
      const labelHandle = handleize(label);
      const keys = new Set([labelHandle, hrefHandle(link), handleize(link?.id || ''), handleize(link?.getAttribute?.('aria-controls') || '')]);
      keys.delete('');

      return {
        li,
        link,
        label,
        keys,
        grid: li.querySelector(':scope > .menu-list__submenu .mega-menu__grid'),
      };
    })
    .filter((entry) => entry.label || entry.keys.size);
}

function buildDrawerEntries() {
  const entries = [];
  document.querySelectorAll('.menu-drawer__menu.has-submenu > li, .menu-drawer__menu > li').forEach((li) => {
    const textEl = li.querySelector('.menu-drawer__menu-item-text');
    if (!textEl) return;
    const label = textEl.textContent?.trim() || '';
    const keys = new Set([handleize(label)]);
    entries.push({
      li,
      textEl,
      label,
      keys,
      childlist: li.querySelector('details .menu-drawer__menu--childlist'),
    });
  });
  return entries;
}

function findEntry(entries, target) {
  const cleanTarget = handleize(target);
  if (!cleanTarget) return null;

  const exact = entries.find((entry) => entry.keys.has(cleanTarget));
  if (exact) return exact;

  const fuzzy = entries.filter((entry) => {
    const keys = [...entry.keys];
    return keys.some((key) => key.includes(cleanTarget) || cleanTarget.includes(key));
  });

  return fuzzy.length === 1 ? fuzzy[0] : null;
}

function zoneFor(container, tag = 'div') {
  let zone = container.querySelector(':scope > .pf-mega-zone');
  if (!zone) {
    zone = document.createElement(tag);
    zone.className = 'pf-mega-zone';
    container.appendChild(zone);
  }
  return zone;
}

function replaceNativeMegaGrid(grid) {
  if (!grid) return;

  [...grid.children].forEach((child) => {
    if (child.classList?.contains('pf-mega-zone')) return;
    child.setAttribute('data-pf-native-mega-hidden', 'true');
    child.hidden = true;
  });

  grid.classList.add('pf-mega-grid--replaced');
  grid.dataset.pfMegaNativeReplaced = 'true';
}

function synthesizePanel(entry) {
  if (!entry?.li) return null;

  let grid = entry.li.querySelector(':scope > .menu-list__submenu .mega-menu__grid');
  if (grid) return grid;

  const trigger = entry.link || directMenuLink(entry.li);
  const uid = 'pf-synthetic-submenu-' + (entry.label ? handleize(entry.label) : Math.random().toString(36).slice(2, 9));

  const sub = document.createElement('div');
  sub.className = 'menu-list__submenu pf-synthetic pf-mega-panel';
  sub.setAttribute('ref', 'submenu[]');

  const inner = document.createElement('div');
  inner.id = uid;
  inner.className = 'menu-list__submenu-inner';

  const mega = document.createElement('div');
  mega.className = 'mega-menu section section--full-width-margin';

  grid = document.createElement('div');
  grid.className = 'mega-menu__grid pf-synthetic-grid';

  mega.appendChild(grid);
  inner.appendChild(mega);
  sub.appendChild(inner);
  entry.li.appendChild(sub);

  if (trigger) {
    trigger.setAttribute('aria-controls', uid);
    trigger.setAttribute('aria-haspopup', 'true');
    if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
  }

  entry.grid = grid;
  return grid;
}

function cloneMegaSource(source) {
  const clone = source.cloneNode(true);
  clone.dataset.pfClone = 'true';
  clone.dataset.pfSourceId = source.dataset.pfId || '';
  clone.removeAttribute('hidden');
  clone.style.removeProperty('display');
  return clone;
}

function placeDesktopMega(source, entries) {
  const target = source.dataset.pfTarget || source.dataset.pfTargetLabel || '';
  const uid = source.dataset.pfId || source.dataset.pfSourceId || '';
  const shouldReplaceNative = source.dataset.pfReplace === 'true';

  const targetEntries = target
    ? [findEntry(entries, target)].filter(Boolean)
    : entries.filter((entry) => entry.grid);

  if (!targetEntries.length) {
    if (target) {
      console.warn(
        `[pf-mega] Mega Menú sin destino: "${source.dataset.pfTargetLabel || target}".`,
        '\nTargets desktop:', entries.map((entry) => entry.label)
      );
    }
    return false;
  }

  let placed = false;
  targetEntries.forEach((entry) => {
    const grid = entry.grid || synthesizePanel(entry);
    if (!grid) return;

    const panel = grid.closest('.menu-list__submenu');
    if (panel) {
      panel.classList.add('pf-mega-panel');
      panel.style.setProperty('--pf-mega-panel-bg', source.dataset.pfPanelBg || '#ffffff');
      panel.style.setProperty('--pf-mega-panel-text', source.dataset.pfPanelText || 'rgb(var(--color-foreground, 18 18 18))');
    }

    if (shouldReplaceNative) replaceNativeMegaGrid(grid);

    const zone = zoneFor(grid);
    if (uid && zone.querySelector(`[data-pf-source-id="${CSS.escape(uid)}"], [data-pf-id="${CSS.escape(uid)}"]`)) {
      entry.li.classList.add('pf-mega-ready');
      return;
    }

    zone.appendChild(cloneMegaSource(source));
    entry.li.classList.add('pf-mega-ready');
    placed = true;
  });

  return placed;
}

function placeDrawerMega(source, entries) {
  if (source.dataset.pfDrawer === 'false') return false;

  const target = source.dataset.pfTarget || source.dataset.pfTargetLabel || '';
  const uid = source.dataset.pfId || source.dataset.pfSourceId || '';
  const targetEntries = target ? [findEntry(entries, target)].filter(Boolean) : entries.filter((entry) => entry.childlist);
  let placed = false;

  targetEntries.forEach((entry) => {
    const placedParent = entry.childlist || entry.li.parentElement;
    if (!placedParent || (uid && placedParent.querySelector(`[data-pf-source-id="${CSS.escape(uid)}"], [data-pf-id="${CSS.escape(uid)}"]`))) return;

    const det = document.createElement('details');
    det.className = 'pf-drawer-mega';

    const sum = document.createElement('summary');
    sum.className = 'pf-drawer-mega__summary';
    sum.innerHTML =
      '<span>' + (source.dataset.pfDrawerTitle || 'Destacados') + '</span>' +
      '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M2 4.5 7 9.5l5-5"/></svg>';

    det.appendChild(sum);
    det.appendChild(cloneMegaSource(source));

    const li = document.createElement('li');
    li.className = 'pf-mega-zone';
    li.appendChild(det);

    if (entry.childlist) entry.childlist.appendChild(li);
    else entry.li.after(li);

    placed = true;
  });

  return placed;
}

function setupMegaHover() {
  if (!window.matchMedia('(hover: hover)').matches) return;
  const menu = document.querySelector('.header-menu');
  if (!menu || menu.dataset.pfHoverBound === 'v11') return;
  menu.dataset.pfHoverBound = 'v11';

  let closeTimer = null;
  const items = () => [...menu.querySelectorAll('.menu-list__list-item.pf-mega-ready')];

  const openOnly = (target) => {
    updateMegaPanelPosition();
    window.clearTimeout(closeTimer);
    items().forEach((li) => {
      const active = li === target;
      li.classList.toggle('pf-mega-open', active);
      directMenuLink(li)?.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
  };

  const closeAll = () => {
    items().forEach((li) => {
      li.classList.remove('pf-mega-open');
      directMenuLink(li)?.setAttribute('aria-expanded', 'false');
    });
  };

  menu.addEventListener('pointerover', (event) => {
    const li = event.target.closest('.menu-list__list-item.pf-mega-ready');
    if (!li || !menu.contains(li)) return;
    openOnly(li);
  });

  menu.addEventListener('pointerleave', () => {
    closeTimer = window.setTimeout(closeAll, 140);
  });

  menu.addEventListener('pointerenter', () => window.clearTimeout(closeTimer));
}

function placeMegaBlocks() {
  ensureMegaStyle();
  updateMegaPanelPosition();

  const sources = [...document.querySelectorAll('.pf-mega-menu[data-pf-block]:not([data-pf-clone="true"])')];
  if (!sources.length) return false;

  const desktopEntries = buildDesktopEntries();
  const drawerEntries = buildDrawerEntries();
  let placedAny = false;

  sources.forEach((source) => {
    if (source.children.length > 6) {
      console.warn(
        `[pf-mega] "${source.dataset.pfTargetLabel || source.dataset.pfTarget || 'Mega'}": máx. 6 columnas — las adicionales quedan ocultas.`
      );
    }

    placedAny = placeDesktopMega(source, desktopEntries) || placedAny;
    placedAny = placeDrawerMega(source, drawerEntries) || placedAny;
  });

  setupMegaHover();
  return placedAny;
}

let pfMegaScheduled = false;
function runMegaPlacement(reason = 'manual') {
  try {
    placeMegaBlocks();
  } catch (error) {
    console.warn(`[pf-mega] Error al ubicar megamenú (${reason}).`, error);
  }
}

function scheduleMegaPlacement(reason = 'scheduled', delay = 50) {
  if (pfMegaScheduled) return;
  pfMegaScheduled = true;
  window.setTimeout(() => {
    pfMegaScheduled = false;
    runMegaPlacement(reason);
  }, delay);
}

function burstMegaPlacement(reason = 'burst') {
  [0, 50, 150, 350, 700, 1200, 2000, 3200].forEach((delay) => {
    window.setTimeout(() => runMegaPlacement(`${reason}:${delay}`), delay);
  });
}

function bindMegaObserver() {
  if (window.__jd1PfMegaObserverBoundV11) return;
  window.__jd1PfMegaObserverBoundV11 = true;

  const observer = new MutationObserver((mutations) => {
    const shouldRun = mutations.some((mutation) =>
      [...mutation.addedNodes].some((node) => {
        if (node.nodeType !== 1) return false;
        const el = /** @type {Element} */ (node);
        return (
          el.matches?.('.header-menu, .pf-nav-actions, .pf-mega-menu, .menu-drawer__menu, header-component, #header-component') ||
          el.querySelector?.('.header-menu, .pf-nav-actions, .pf-mega-menu, .menu-drawer__menu, header-component, #header-component')
        );
      })
    );

    if (shouldRun) scheduleMegaPlacement('mutation', 80);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

bindMegaObserver();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => burstMegaPlacement('dom-ready'));
} else {
  burstMegaPlacement('dom-ready');
}

window.addEventListener('load', () => burstMegaPlacement('window-load'));
window.addEventListener('pageshow', () => burstMegaPlacement('pageshow'));
window.addEventListener('resize', () => scheduleMegaPlacement('resize', 80));
window.addEventListener('scroll', () => updateMegaPanelPosition(), { passive: true });

[
  'shopify:section:load',
  'shopify:section:select',
  'shopify:block:select',
  'shopify:section:reorder',
  'theme:section:load',
  'theme:section:select',
  'section-hydration:complete',
].forEach((eventName) => {
  document.addEventListener(eventName, () => burstMegaPlacement(eventName));
});